import { useCallback } from 'react';
import { tickOverridesEqual } from '../../../utils/tickOverrides';
import { docVariablesEqual } from '../utils/docVariablesUtils';

function restoreSnapshot({
  snapshot,
  commit,
  applyDocVariables,
  applyTickOverrides,
  docVariablesRef,
  onDocVariablesChangeRef,
  tickOverridesRef,
  onTickOverridesChangeRef,
}) {
  const blocksSnap = snapshot?.blocks ?? snapshot;
  const varsSnap = snapshot?.docVariables;
  const ticksSnap = snapshot?.tickOverrides;
  commit(blocksSnap, { record: false });
  if (
    onDocVariablesChangeRef.current &&
    varsSnap !== undefined &&
    !docVariablesEqual(docVariablesRef.current, varsSnap)
  ) {
    applyDocVariables(varsSnap, { recordEpoch: true });
  }
  if (
    onTickOverridesChangeRef.current &&
    ticksSnap !== undefined &&
    !tickOverridesEqual(tickOverridesRef.current, ticksSnap)
  ) {
    applyTickOverrides(ticksSnap);
  }
}

export default function useUndoRedo({
  historyRef,
  captureSnapshot,
  commit,
  applyDocVariables,
  applyTickOverrides,
  docVariablesRef,
  onDocVariablesChangeRef,
  tickOverridesRef,
  onTickOverridesChangeRef,
}) {
  const undo = useCallback(() => {
    const h = historyRef.current;
    if (!h.undo.length) return false;
    const snapshot = h.undo.pop();
    h.redo.push(captureSnapshot());
    h.lastRecord = 0; // next edit starts a fresh undo step
    restoreSnapshot({
      snapshot,
      commit,
      applyDocVariables,
      applyTickOverrides,
      docVariablesRef,
      onDocVariablesChangeRef,
      tickOverridesRef,
      onTickOverridesChangeRef,
    });
    return true;
  }, [
    historyRef,
    captureSnapshot,
    commit,
    applyDocVariables,
    applyTickOverrides,
    docVariablesRef,
    onDocVariablesChangeRef,
    tickOverridesRef,
    onTickOverridesChangeRef,
  ]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (!h.redo.length) return false;
    const snapshot = h.redo.pop();
    h.undo.push(captureSnapshot());
    h.lastRecord = 0;
    restoreSnapshot({
      snapshot,
      commit,
      applyDocVariables,
      applyTickOverrides,
      docVariablesRef,
      onDocVariablesChangeRef,
      tickOverridesRef,
      onTickOverridesChangeRef,
    });
    return true;
  }, [
    historyRef,
    captureSnapshot,
    commit,
    applyDocVariables,
    applyTickOverrides,
    docVariablesRef,
    onDocVariablesChangeRef,
    tickOverridesRef,
    onTickOverridesChangeRef,
  ]);

  return { undo, redo };
}
